/**
 * GameNotesField — the shared free-text "Notes" input used by the Propose,
 * Record and Edit game forms so all three look and behave identically. Whatever
 * is entered is appended to the game's notes history (a running log the system
 * also writes dispute / resolution / reschedule entries to), never overwriting
 * prior entries — so on the Edit form this reads as "add a note", not "replace".
 */
import React from 'react';

interface GameNotesFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Section heading, e.g. "3 · Notes" — numbering differs per form. */
  label: string;
  /** Optional placeholder tailored to the form. */
  placeholder?: string;
  /** True on the Edit form, where the field adds to an existing history. */
  appendMode?: boolean;
}

const GameNotesField: React.FC<GameNotesFieldProps> = ({
  value,
  onChange,
  label,
  placeholder,
  appendMode,
}) => (
  <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
    <h2 className="text-sm font-semibold text-gray-700">{label}</h2>
    <label htmlFor="game-notes" className="mt-3 block text-sm font-medium text-gray-700">
      {appendMode ? 'Add a note' : 'Notes'}{' '}
      <span className="text-gray-400 font-normal">(optional)</span>
    </label>
    <textarea
      id="game-notes"
      rows={3}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder ?? 'Add a note about this game…'}
      maxLength={500}
      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      aria-label="Notes"
    />
    <p className="mt-1 text-xs text-gray-400">
      Added to the game&apos;s notes history, visible to both teams.
    </p>
  </section>
);

export default GameNotesField;
